// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { getScrollableParents } from '../internal/utils/scrollable-containers';
const AUTO_SKELETON_VIEWPORT_BUFFER = 16;
const SKELETON_ROW_SELECTOR = 'tr[aria-hidden="true"]';
function getDocumentViewportBottom() {
    return document.documentElement.clientHeight || window.innerHeight;
}
function getViewportBottom(element) {
    return getScrollableParents(element).reduce((viewportBottom, scrollContainer) => Math.min(viewportBottom, scrollContainer.getBoundingClientRect().bottom), getDocumentViewportBottom());
}
function getOverflowHeight(element) {
    const scrollContainer = getScrollableParents(element)[0];
    if (scrollContainer) {
        return Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
    }
    return Math.max(0, Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) - getDocumentViewportBottom());
}
function calculateAutoSkeletonRows({ maxRows, minRows, rowHeight, skeletonRowBottom, skeletonRowTop, tableBottom, viewportBottom, }) {
    const staticHeightBelowSkeletonRow = Math.max(0, tableBottom - skeletonRowBottom);
    const availableHeight = Math.max(0, viewportBottom - skeletonRowTop - staticHeightBelowSkeletonRow - AUTO_SKELETON_VIEWPORT_BUFFER);
    const rows = Math.floor(availableHeight / rowHeight);
    return Math.max(minRows, Math.min(maxRows !== null && maxRows !== void 0 ? maxRows : Number.POSITIVE_INFINITY, rows));
}
function calculateAutoSkeletonRowReduction({ currentRows, minRows, overflowHeight, rowHeight, }) {
    return Math.min(currentRows - minRows, Math.max(1, Math.ceil((overflowHeight + AUTO_SKELETON_VIEWPORT_BUFFER) / rowHeight)));
}
export function useAutoSkeletonRows({ enabled, maxRows, minRows, tableBodyRef, tableRootRef, tableWrapperRef, }) {
    const maxRowsValue = maxRows === undefined ? undefined : Math.max(1, maxRows);
    const minRowsValue = Math.min(Math.max(1, minRows !== null && minRows !== void 0 ? minRows : 1), maxRowsValue !== null && maxRowsValue !== void 0 ? maxRowsValue : Number.POSITIVE_INFINITY);
    const [rows, setRows] = useState(minRowsValue);
    const rowHeightRef = useRef();
    const updateRows = useCallback(() => {
        var _a, _b, _c, _d;
        if (!enabled) {
            return;
        }
        const tableRoot = tableRootRef.current;
        const tableWrapper = tableWrapperRef.current;
        const skeletonRows = (_a = tableBodyRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(SKELETON_ROW_SELECTOR);
        const firstSkeletonRowRect = (_b = skeletonRows === null || skeletonRows === void 0 ? void 0 : skeletonRows[0]) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        const lastSkeletonRowRect = (_c = skeletonRows === null || skeletonRows === void 0 ? void 0 : skeletonRows[skeletonRows.length - 1]) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect();
        const rowHeight = (_d = lastSkeletonRowRect === null || lastSkeletonRowRect === void 0 ? void 0 : lastSkeletonRowRect.height) !== null && _d !== void 0 ? _d : rowHeightRef.current;
        if (!tableRoot || !tableWrapper || !firstSkeletonRowRect || !lastSkeletonRowRect || !rowHeight) {
            return;
        }
        rowHeightRef.current = rowHeight;
        const nextRows = calculateAutoSkeletonRows({
            maxRows: maxRowsValue,
            minRows: minRowsValue,
            rowHeight,
            skeletonRowBottom: lastSkeletonRowRect.bottom,
            skeletonRowTop: firstSkeletonRowRect.top,
            tableBottom: tableRoot.getBoundingClientRect().bottom,
            viewportBottom: getViewportBottom(tableWrapper),
        });
        setRows(currentRows => (currentRows === nextRows ? currentRows : nextRows));
    }, [enabled, maxRowsValue, minRowsValue, tableBodyRef, tableRootRef, tableWrapperRef]);
    useLayoutEffect(updateRows, [updateRows]);
    useLayoutEffect(() => {
        const tableWrapper = tableWrapperRef.current;
        const rowHeight = rowHeightRef.current;
        if (!enabled || rows <= minRowsValue || !tableWrapper || !rowHeight) {
            return;
        }
        const overflowHeight = getOverflowHeight(tableWrapper);
        if (overflowHeight === 0) {
            return;
        }
        const rowsToRemove = calculateAutoSkeletonRowReduction({
            currentRows: rows,
            minRows: minRowsValue,
            overflowHeight,
            rowHeight,
        });
        setRows(currentRows => currentRows - rowsToRemove);
    }, [enabled, rows, minRowsValue, tableWrapperRef]);
    useLayoutEffect(() => {
        if (!enabled || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const tableRoot = tableRootRef.current;
        const tableWrapper = tableWrapperRef.current;
        if (!tableRoot || !tableWrapper) {
            return undefined;
        }
        const resizeObserver = new ResizeObserver(updateRows);
        resizeObserver.observe(tableRoot);
        resizeObserver.observe(tableWrapper);
        getScrollableParents(tableWrapper).forEach(scrollContainer => resizeObserver.observe(scrollContainer));
        window.addEventListener('resize', updateRows);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateRows);
        };
    }, [enabled, tableRootRef, tableWrapperRef, updateRows]);
    return rows;
}
//# sourceMappingURL=use-auto-skeleton-rows.js.map